import { useCallback, useEffect, useState } from 'react';
import Table, { Order } from '../../../components/Table';
import { EScope, IPostModel } from '../../../models/post.model';
import { IPaginationData } from '../../../api/base';
import postApi from '../../../api/post.api';
import moment from 'moment';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { Add, Delete, Edit, Search } from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { PostItemImage } from '../../styled';
import { toast } from 'react-toastify';

export default function BlogListPage() {
  const [dataSource, setDataSource] = useState<IPaginationData<IPostModel>>({
    limit: 10,
    page: 1,
    total: 0,
    total_page: 0,
    records: [],
  });

  const [filter, setFilter] = useState({
    keyword: '',
    page: 1,
    limit: 10,
  });

  const [loading, setLoading] = useState(false);
  const [[sortBy, order], setOrder] = useState<
    [string | undefined, Order | undefined]
  >([undefined, 'asc']);

  const [currDeletedId, setCurrDeletedId] = useState<string | undefined>();
  const [deleting, setDeleting] = useState(false);

  const loadData = useCallback(() => {
    setLoading(true);
    postApi
      .getList({
        ...filter,
        scope: EScope.News,
        order: order?.toUpperCase() as 'ASC' | 'DESC',
        sort_by: sortBy,
      })
      .then((res) => {
        setDataSource(res.data);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [sortBy, order, filter]);

  useEffect(loadData, [loadData]);

  const theme = useTheme();

  return (
    <>
      <Box
        sx={(theme) => ({
          display: 'flex',
          justifyContent: 'space-between',
          p: 2,
          borderBottom: `1px solid ${theme.palette.divider}`,
        })}
      >
        <Typography variant="h3">Blogs</Typography>
        <div
          style={{
            display: 'flex',
            gap: theme.spacing(4),
          }}
        >
          <form
            onSubmit={(e) => {
              e.preventDefault();

              const formData = new FormData(e.currentTarget);
              setFilter({
                ...filter,
                page: 1,
                keyword: (formData.get('search') || '').toString(),
              });
            }}
          >
            <TextField
              placeholder="Search"
              variant="outlined"
              size="small"
              name="search"
              sx={{
                '& .MuiInputBase-root': {
                  pr: 0,
                },
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Button
                      type="submit"
                      sx={{ minWidth: 48, borderRadius: 0 }}
                    >
                      <Search />
                    </Button>
                  </InputAdornment>
                ),
              }}
            />
          </form>
          <Link to="create">
            <Button variant="contained" color="success" startIcon={<Add />}>
              Create new
            </Button>
          </Link>
        </div>
      </Box>
      <Table
        onOrderChange={(orderBy, order) => setOrder([orderBy!, order!])}
        order={order}
        orderBy={sortBy}
        loading={loading}
        pagination={{
          limit: dataSource.limit,
          total: dataSource.total,
          page: dataSource.page - 1,
          onPageChange: (event, page) => {
            setFilter({
              ...filter,
              page: page + 1,
            });
          },
          onLimitChange: (ev) => {
            setFilter({
              ...filter,
              limit: +ev.target.value,
              page: 1,
            });
          },
        }}
        columns={[
          {
            key: 'order',
            title: 'Order',
            dataKey: 'order',
            sortable: true,
            render: (data) => data.order,
          },
          {
            key: 'picture',
            title: 'Picture',
            dataKey: 'picture',
            render: (data) =>
              data.picture ? (
                <PostItemImage
                  src={process.env.REACT_APP_API_URI + data.picture}
                />
              ) : null,
          },
          {
            key: 'title',
            title: 'Title',
            dataKey: '',
            render: (data) => (
              <>
                <div>{data.title_vi}</div>
                <div>{data.title_en}</div>
              </>
            ),
          },
          // {
          //   key: 'category',
          //   title: 'Category',
          //   dataKey: 'category',
          //   sortable: true,
          //   render: (data) => data.category?.title,
          // },
          {
            key: 'created_at',
            title: 'Created at',
            dataKey: 'created_at',
            sortable: true,
            render: (data) =>
              moment(data.created_at).format('DD/MM/YYYY HH:mm:ss'),
          },
          {
            key: 'updated_at',
            title: 'Updated at',
            dataKey: 'updated_at',
            sortable: true,
            render: (data) =>
              moment(data.updated_at).format('DD/MM/YYYY HH:mm:ss'),
          },

          // {
          //   key: 'published_at',
          //   title: 'Published at',
          //   dataKey: 'published_at',
          //   render: (data) =>
          //     moment(data.published_at).format('DD/MM/YYYY HH:mm:ss'),
          // },
          {
            key: 'visible',
            title: 'Visible',
            dataKey: 'visible',
            sortable: true,
            render: (data) => (
              <Checkbox
                onChange={(ev, value) => {
                  const idx = dataSource.records.indexOf(data);
                  postApi.update(data.id, { visible: value }).then((res) => {
                    const records = Array.from(dataSource.records);
                    records[idx] = {
                      ...data,
                      visible: res.data.visible,
                    };
                    setDataSource({
                      ...dataSource,
                      records,
                    });
                  });
                }}
                checked={data.visible}
                readOnly
              />
            ),
          },
          {
            key: 'action',
            title: '',
            dataKey: 'id',
            render: (data) => (
              <>
                <Link to={data.id}>
                  <IconButton>
                    <Edit />
                  </IconButton>
                </Link>
                <IconButton onClick={() => setCurrDeletedId(data.id)}>
                  <Delete />
                </IconButton>
              </>
            ),
          },
        ]}
        dataSource={dataSource.records}
        rowKey={(data) => data.id}
      />
      <Dialog
        open={!!currDeletedId}
        onClose={() => setCurrDeletedId(undefined)}
      >
        <DialogContent>
          <DialogContentText color={'black'}>
            This action cannot be revert. Do you want to delete this post?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            autoFocus
            disabled={deleting}
            onClick={() => setCurrDeletedId(undefined)}
            size="small"
          >
            Cancel
          </Button>
          <Button
            size="small"
            disabled={deleting}
            onClick={() => {
              if (currDeletedId) {
                setDeleting(true);
                postApi
                  .remove(currDeletedId)
                  .then(() => {
                    loadData();
                    toast.success('Delete post successful');
                  })
                  .catch((e) => {
                    toast.error(e.message ?? e.toString());
                  })
                  .finally(() => {
                    setCurrDeletedId(undefined);
                    setDeleting(false);
                  });
              }
            }}
            color="error"
          >
            {deleting ? <CircularProgress size={16} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
